import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import type {
  IndicatorRegisterItem,
  IndicatorRegisterResponse,
} from '@risksmart-app/trpc/src/types';
import type { GetIndicatorsQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useTRPC } from 'src/utils/trpc';

import { useIsFeatureVisibleToOrg } from '@/utils/featureFlags';

function mapTrpcIndicatorToGraphQL(
  indicator: IndicatorRegisterItem
): GetIndicatorsQuery['indicator'][number] {
  return {
    ...indicator,
  };
}

/**
 * Maps TRPC indicator data to match the GraphQL query structure
 */
export function mapTrpcIndicatorsToGraphQL(
  trpcData: IndicatorRegisterResponse | undefined
): GetIndicatorsQuery | undefined {
  if (!trpcData) {
    return undefined;
  }

  return {
    indicator: trpcData.indicators.map((indicator) =>
      mapTrpcIndicatorToGraphQL(indicator)
    ),
  };
}

export const useGetIndicatorRegister = (skip?: boolean) => {
  const trpcEnabled = useIsFeatureVisibleToOrg('trpc');
  const trpc = useTRPC();
  const { addNotification } = useNotifications();
  const {
    data: trpcData,
    isLoading: trpcLoading,
    refetch: trpcRefetch,
    error: trpcError,
  } = useQuery({
    ...trpc.frontend.indicator.register.queryOptions(),

    enabled: trpcEnabled && !skip, // Only enable TRPC query when flag is true
  });

  // Handle TRPC errors
  useEffect(() => {
    if (trpcEnabled && trpcError) {
      addNotification({
        type: 'error',
        content: <>{trpcError.message}</>,
      });
    }
  }, [trpcEnabled, trpcError, addNotification]);

  // Transform TRPC data to match the same structure as GraphQL query
  const data = mapTrpcIndicatorsToGraphQL(trpcData);

  return {
    loading: trpcLoading,
    data,
    refetch: () => trpcRefetch(),
  };
};
